import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency';
import { Plus, Target, Pencil, Trash2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const statusLabels = { active: 'Activa', completed: 'Concluída', paused: 'Pausada', cancelled: 'Cancelada' };
const statusBadge = { active: 'badge-active', completed: 'badge-completed', paused: 'badge-paused', cancelled: 'badge-expense' };
const priorityColors = { high: '#EF4444', medium: '#F59E0B', low: '#10B981' };

export default function Goals() {
  const { cardShape } = useTheme();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', target_amount: '', deadline: '', icon: '🎯', color: '#4F8EF7', priority: 'medium' });
  const navigate = useNavigate();

  const fetchGoals = async () => {
    setLoading(true);
    const res = await api.get('/goals');
    setGoals(res.data);
    setLoading(false);
  };

  useEffect(() => { fetchGoals(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, target_amount: parseFloat(form.target_amount) };
    if (editGoal) {
      await api.put(`/goals/${editGoal.id}`, payload);
    } else {
      await api.post('/goals', payload);
    }
    setShowForm(false); setEditGoal(null);
    fetchGoals();
  };

  const handleDelete = async (id) => {
    if (confirm('Eliminar esta meta?')) {
      await api.delete(`/goals/${id}`);
      fetchGoals();
    }
  };

  return (
    <div>
      <div className="page-header" style={{ flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Metas Financeiras</h1>
          <p className="page-subtitle">{goals.filter(g => g.status === 'active').length} activas</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditGoal(null); setForm({ name: '', description: '', target_amount: '', deadline: '', icon: '🎯', color: '#4F8EF7', priority: 'medium' }); setShowForm(true); }}>
          <Plus size={18} /> Nova Meta
        </button>
      </div>

      {loading ? (
        <div className="grid-auto">{[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 180, borderRadius: 16 }} />)}</div>
      ) : goals.length === 0 ? (
        <div className="card"><div className="empty-state"><div className="empty-state-icon"><Target size={28} /></div><p className="empty-state-title">Sem metas</p><p className="empty-state-text">Defina as suas metas financeiras.</p></div></div>
      ) : (
        <div className="grid-auto">
          {goals.map(goal => {
            const pct = parseFloat(goal.progress || 0);
            return (
              <div key={goal.id} className={`goal-card variant-${cardShape}`} style={{ cursor: 'pointer', position: 'relative' }}
                onClick={() => navigate(`/goals/${goal.id}`)}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: goal.color, opacity: 0.6 }} />
                <div className="flex-between mb-sm">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{goal.icon}</span>
                    <span style={{ fontSize: 16, fontWeight: 700 }}>{goal.name}</span>
                  </div>
                  <span className={`badge ${statusBadge[goal.status]}`}>{statusLabels[goal.status]}</span>
                </div>

                {goal.description && (
                  <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 12 }}>{goal.description}</p>
                )}

                {/* Donut-like progress */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                  <div style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
                    <svg width="56" height="56" viewBox="0 0 56 56">
                      <circle cx="28" cy="28" r="24" stroke="var(--bg-input)" strokeWidth="5" fill="none" />
                      <circle cx="28" cy="28" r="24" stroke={goal.color} strokeWidth="5" fill="none"
                        strokeDasharray={`${pct * 1.508} 150.8`} strokeLinecap="round"
                        transform="rotate(-90 28 28)" style={{ transition: 'stroke-dasharray 0.5s' }} />
                    </svg>
                    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                      {pct.toFixed(0)}%
                    </span>
                  </div>
                  <div>
                    <div className="text-mono" style={{ fontSize: 18, fontWeight: 700 }}>{formatCurrency(goal.current_amount)}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>de {formatCurrency(goal.target_amount)}</div>
                  </div>
                </div>

                <div className="flex-between">
                  <div style={{ display: 'flex', gap: 4 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: priorityColors[goal.priority] }} />
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{{ high: 'Alta', medium: 'Média', low: 'Baixa' }[goal.priority]}</span>
                  </div>
                  {goal.deadline && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Prazo: {new Date(goal.deadline).toLocaleDateString('pt-AO')}</span>
                  )}
                </div>

                <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-icon" style={{ width: 28, height: 28 }} onClick={(e) => { e.stopPropagation(); setEditGoal(goal); setForm(goal); setShowForm(true); }}>
                    <Pencil size={12} />
                  </button>
                  <button className="btn btn-ghost btn-icon" style={{ width: 28, height: 28 }} onClick={(e) => { e.stopPropagation(); handleDelete(goal.id); }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editGoal ? 'Editar Meta' : 'Nova Meta'}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nome</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Descrição</label>
                  <input className="form-input" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Objectivo (Kz)</label>
                    <input className="form-input form-input-money" type="number" step="0.01" value={form.target_amount} onChange={e => setForm({ ...form, target_amount: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Prazo</label>
                    <input className="form-input" type="date" value={form.deadline || ''} onChange={e => setForm({ ...form, deadline: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Prioridade</label>
                    <select className="form-input form-select" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                      <option value="high">Alta</option>
                      <option value="medium">Média</option>
                      <option value="low">Baixa</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cor</label>
                    <input type="color" className="form-input" style={{ height: 42, padding: 4 }} value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editGoal ? 'Guardar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
